// packages/supabase-client/src/react/forms/SignUpForm.tsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button, TextField } from '@dopomogai/ui';
import { validateEmail, validatePassword, validatePasswordMatch } from '../utils/validation';
import { AuthFormData, FormErrors } from '../types/form';
import { useAuthText, useAuthUI } from '../hooks';
import { AuthAlert } from '../components';

export interface SignUpFormProps {
  onSubmit: (data: Pick<AuthFormData, 'email' | 'password'>) => Promise<void>;
  isPending?: boolean;
  // --- UPDATED: The error message can now be a string or a full React component ---
  errorMessage?: React.ReactNode | null;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSubmit, isPending, errorMessage }) => {
  const { t } = useTranslation(['auth', 'common']);
  const { text, resolveText, resolveString } = useAuthText();
  const { routes } = useAuthUI();
  const signUpText = resolveText(text.signUpButton, 'auth:signUpButton');
  const signUpTextIsString = typeof signUpText === 'string' || typeof signUpText === 'number';

  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field: keyof AuthFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validatePasswordMatch(formData.password, formData.confirmPassword!);

    if (emailError) newErrors.email = t(emailError, { ns: 'common' });
    if (passwordError) newErrors.password = t(passwordError, { ns: 'common' });
    if (confirmPasswordError) newErrors.confirmPassword = t(confirmPasswordError, { ns: 'common' });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSubmit({ email: formData.email, password: formData.password });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && <AuthAlert message={errorMessage} />}
      <TextField
        label={resolveString(text.emailLabel, 'common:email')}
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        placeholder={resolveString(text.emailPlaceholder, 'auth:emailPlaceholder')}
        leadingIcon={<Mail className="w-5 h-5 text-slate-400" />}
        errorText={errors.email}
        disabled={isPending}
      />
      <TextField
        label={resolveString(text.passwordLabel, 'common:password')}
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)}
        placeholder={resolveString(text.passwordPlaceholder, 'auth:passwordPlaceholder')}
        leadingIcon={<Lock className="w-5 h-5 text-slate-400" />}
        trailingIcon={
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-slate-600 focus:outline-none" aria-label={t('togglePasswordVisibility', { ns: 'auth', defaultValue: 'Toggle password visibility' })}>
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        }
        errorText={errors.password}
        disabled={isPending}
      />
      <TextField
        label={resolveString(text.confirmPasswordLabel, 'auth:confirmPassword')}
        type={showPassword ? 'text' : 'password'}
        value={formData.confirmPassword || ''}
        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
        placeholder={resolveString(text.confirmPasswordPlaceholder, 'auth:confirmPasswordPlaceholder')}
        leadingIcon={<Lock className="w-5 h-5 text-slate-400" />}
        errorText={errors.confirmPassword}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        fullWidth
        text={signUpTextIsString ? String(signUpText) : undefined}
        disabled={isPending}
        loading={isPending}
      >
        {!signUpTextIsString ? signUpText : null}
      </Button>
      <div className="text-center">
        <span className="text-slate-600">{resolveText(text.alreadyAccount, 'auth:alreadyAccount')} </span>
        <Link to={routes.login} className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
          {resolveText(text.signInLink, 'auth:signInLink')}
        </Link>
      </div>
    </form>
  );
};
